import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  useBalances, useAlerts, useMovements, useTransfers, useAdjustments,
  useTransferAction, useAdjustmentAction,
  useLocations, useCreateLocation, useCreateTransfer, useCreateAdjustment,
  useCreateReorderPO,
} from '../hooks/useInventoryApi';
import { Card, CardHeader, CardBody } from '@/shared/components/ui/Card';
import { Button } from '@/shared/components/ui/Button';
import { Input } from '@/shared/components/ui/Input';
import { Modal } from '@/shared/components/ui/Modal';
import { Select } from '@/shared/components/ui/Select';
import { Skeleton } from '@/shared/components/ui/Skeleton';
import { useUser } from '@/shared/hooks/useAuth';
import {
  Package, AlertTriangle, ArrowLeftRight, ClipboardCheck, History, MapPin, Plus, ShoppingCart,
  Truck, ClipboardList, Warehouse, TrendingUp, Clock, Search as SearchIcon, Boxes,
  ArrowDownToLine, Droplets, Layers, ChevronDown, Gauge,
} from 'lucide-react';
import toast from 'react-hot-toast';
import {
  TransitTab, CountsTab, BinsTab, ReorderTab, AgingTab, TraceabilityTab,
} from '../components/AdvancedInventoryTabs';
import { MaterialInwardTab } from '../components/MaterialInwardTab';
import { MaterialRequestsTab } from '../components/MaterialRequestsTab';
import { TankRegisterTab } from '../components/TankRegisterTab';
import { TankLevelsTab } from '../components/TankLevelsTab';
import { SuppliersTab } from '../components/SuppliersTab';
import { StockControlTab } from '../components/StockControlTab';
import { PackingBomsTab } from '../components/PackingBomsTab';
import { useStockOverview } from '../hooks/useStockControl';
import { useMaterialRequests, useMaterialInwards } from '../hooks/useMaterials';
import { hasWorkspaceAccess } from '@/shared/permissions/workspaceAccess';
import { localToday } from '@/shared/utils/utils';

function errorMessage(err: unknown, fallback: string): string {
  if (err && typeof err === 'object') {
    const maybe = err as { response?: { data?: { error?: string } } };
    if (maybe.response?.data?.error) return maybe.response.data.error;
  }
  return fallback;
}

/**
 * Structured adjustment reasons. `value` must match the reasonCode enum on the
 * server; `posting` states the resulting GL effect so the person raising the
 * adjustment can see it before saving.
 */
const ADJUSTMENT_REASONS = [
  { value: 'correction', label: 'Count Correction', posting: 'Dr Inventory Loss / Cr Inventory (or the reverse on an increase)' },
  { value: 'loss', label: 'Loss', posting: 'Dr Inventory Loss / Cr Inventory' },
  { value: 'damage', label: 'Damage', posting: 'Dr Inventory Loss / Cr Inventory' },
  { value: 'expiry', label: 'Expiry', posting: 'Dr Inventory Loss / Cr Inventory' },
  { value: 'write_off', label: 'Write-off', posting: 'Dr Inventory Loss / Cr Inventory' },
  { value: 'internal_consumption', label: 'Internal Consumption', posting: 'Dr Consumption Expense / Cr Inventory' },
  { value: 'sample', label: 'Sample / Giveaway', posting: 'Dr Marketing Expense / Cr Inventory' },
  { value: 'gain', label: 'Gain (found stock)', posting: 'Dr Inventory / Cr Inventory Gain' },
  { value: 'recovery', label: 'Recovery', posting: 'Dr Cash / Cr Recovery Income, kept separate from the loss' },
];

/** Facility kinds accepted by POST/PATCH /api/inventory/locations. */
const FACILITY_TYPE_OPTIONS = [
  { label: 'Warehouse', value: 'warehouse' },
  { label: 'Store', value: 'store' },
  { label: 'Distribution Centre', value: 'distribution_center' },
  { label: 'Plant', value: 'plant' },
  { label: 'Production Unit', value: 'production_unit' },
  { label: 'Tank Farm', value: 'tank_farm' },
  { label: 'Branch', value: 'branch' },
  { label: 'Head Office', value: 'headquarters' },
  { label: 'Service Office', value: 'service_office' },
  { label: 'Quarantine', value: 'quarantine' },
  { label: 'In-Transit', value: 'transit' },
  { label: 'Virtual', value: 'virtual' },
];

// Day work first — what Dispatch and the store do every day (client,
// 2026-10-24). The stock-control tools follow under "More", for Inventory.
const PRIMARY_TABS = [
  { id: 'requests', label: 'Material Requests', icon: ClipboardList },
  { id: 'inward', label: 'Material Inward', icon: ArrowDownToLine },
  { id: 'tank-levels', label: 'Tank Levels', icon: Gauge },
  { id: 'tanks', label: 'Tank Register', icon: Droplets },
  { id: 'stock', label: 'Stock Control', icon: Package },
  { id: 'boms', label: 'Packing BOMs', icon: Layers },
  { id: 'suppliers', label: 'Suppliers', icon: Truck },
];
const MORE_TABS = [
  { id: 'warehouse', label: 'Stock by Warehouse', icon: Warehouse },
  { id: 'alerts', label: 'Low Stock Alerts', icon: AlertTriangle },
  { id: 'transfers', label: 'Transfers', icon: ArrowLeftRight },
  { id: 'transit', label: 'In Transit', icon: Truck },
  { id: 'adjustments', label: 'Adjustments', icon: ClipboardCheck },
  { id: 'counts', label: 'Physical Counts', icon: ClipboardList },
  { id: 'locations', label: 'Warehouses / Locations', icon: MapPin },
  { id: 'bins', label: 'Bins & Storage', icon: Warehouse },
  { id: 'reorder', label: 'Reorder Planning', icon: TrendingUp },
  { id: 'aging', label: 'Ageing & Dead Stock', icon: Clock },
  { id: 'trace', label: 'Traceability', icon: SearchIcon },
  { id: 'movements', label: 'Movement History', icon: History },
];
const ALL_TAB_IDS = new Set([...PRIMARY_TABS, ...MORE_TABS].map((t) => t.id));

export function InventoryPage() {
  const user = useUser();
  // Dispatch reaches the day-work tabs; the stock-control tools need Inventory.
  const fullInventory = hasWorkspaceAccess(user, 'inventory');
  const [searchParams, setSearchParams] = useSearchParams();
  const initial = searchParams.get('tab') || 'requests';
  const [tab, setTabState] = useState(ALL_TAB_IDS.has(initial) ? initial : 'requests');
  const setTab = (id: string) => {
    setTabState(id);
    const next = new URLSearchParams(searchParams);
    next.set('tab', id);
    setSearchParams(next, { replace: true });
  };
  const [moreOpen, setMoreOpen] = useState(false);
  const [newRequest, setNewRequest] = useState(0);
  const [newInward, setNewInward] = useState(0);
  const [isTransferModalOpen, setIsTransferModalOpen] = useState(false);
  const [isAdjustmentModalOpen, setIsAdjustmentModalOpen] = useState(false);

  const { data: requestsData } = useMaterialRequests('open', '', false);
  const monthStart = localToday(new Date(new Date().getFullYear(), new Date().getMonth(), 1));
  const { data: inwardData } = useMaterialInwards({ from: monthStart, to: localToday(), category: 'all', q: '' });
  const { data: alertsData } = useAlerts();
  const { data: overview } = useStockOverview();
  const alertCount = alertsData?.alerts?.length || 0;
  const counts = requestsData?.counts || {};
  const awaiting = counts.pending_approval || 0;
  const toIssue = (counts.approved || 0) + (counts.partially_issued || 0);
  const moreTab = MORE_TABS.find((t) => t.id === tab);
  const tabClass = (active: boolean) => `flex items-center gap-2 whitespace-nowrap rounded-t-md border-b-2 px-4 py-2.5 text-body-sm font-semibold transition ${
    active ? 'border-primary bg-primary/5 text-primary' : 'border-transparent text-on-surface-variant hover:bg-surface-container-low hover:text-on-surface'
  }`;

  return (
    <div className="mx-auto max-w-[1580px] space-y-5 pb-16">
      <PageHeader
        icon={Boxes}
        eyebrow="Inventory"
        title="Inventory"
        description="Material requests, receipts at the gate, the tank farm and stock on hand."
        actions={<>
          <Button variant="outline" onClick={() => { setTab('inward'); setNewInward((n) => n + 1); }}>
            <ArrowDownToLine className="h-4 w-4" /> Record inward
          </Button>
          <Button onClick={() => { setTab('requests'); setNewRequest((n) => n + 1); }}>
            <Plus className="h-4 w-4" /> Raise material request
          </Button>
          {fullInventory && (
            <Button variant="outline" onClick={() => setIsAdjustmentModalOpen(true)}>
              <ClipboardCheck className="h-4 w-4" /> Stock adjustment
            </Button>
          )}
        </>}
      />

      <StatGrid cols={4}>
        <StatCard label="Awaiting approval" value={awaiting} hint={requestsData?.canApprove ? 'Requests for you to decide' : 'Requests with Admin'} icon={ClipboardList} tone={awaiting ? 'warning' : 'neutral'} />
        <StatCard label="To issue" value={toIssue} hint="Approved, waiting at the store" icon={Layers} tone={toIssue ? 'info' : 'neutral'} />
        <StatCard label="QC pending" value={overview?.qcPending ?? 0} hint={`${inwardData?.summary.count ?? 0} receipts this month`} icon={ArrowDownToLine} tone={overview?.qcPending ? 'warning' : 'primary'} />
        <StatCard label="Below reorder" value={overview?.belowReorder ?? 0} hint={overview?.negative ? `${overview.negative} items below zero — stock take needed` : `${overview?.expiringSoon ?? 0} batches expire in 30 days`} icon={AlertTriangle} tone={overview?.negative || overview?.belowReorder ? 'danger' : 'neutral'} />
      </StatGrid>

      {/* ── Navigation Tabs ───────────────────────────────────────────────────── */}
      <div className="flex items-end gap-1 border-b border-border">
        <div className="flex min-w-0 flex-1 gap-1 overflow-x-auto">
          {PRIMARY_TABS.map((t) => (
            <button key={t.id} onClick={() => setTab(t.id)} className={tabClass(tab === t.id)}>
              <t.icon className="h-4 w-4" />
              <span>{t.label}</span>
              {t.id === 'requests' && (awaiting + toIssue) > 0 && (
                <span className="rounded-full bg-warning px-1.5 py-0.5 text-[10px] font-bold text-white">{awaiting + toIssue}</span>
              )}
            </button>
          ))}
        </div>
        {fullInventory && (
          <div className="relative">
            <button onClick={() => setMoreOpen((o) => !o)} className={tabClass(!!moreTab)} aria-expanded={moreOpen}>
              {moreTab ? <><moreTab.icon className="h-4 w-4" />{moreTab.label}</> : 'More'}
              <ChevronDown className="h-4 w-4" />
            </button>
            {moreOpen && (
              <div className="absolute right-0 z-30 mt-1 w-60 overflow-hidden rounded-lg border border-border bg-card py-1 shadow-surface-2" onMouseLeave={() => setMoreOpen(false)}>
                {MORE_TABS.map((t) => (
                  <button
                    key={t.id}
                    onClick={() => { setTab(t.id); setMoreOpen(false); }}
                    className={`flex w-full items-center gap-2 px-3 py-2 text-left text-body-sm hover:bg-surface-container-low ${tab === t.id ? 'font-semibold text-primary' : 'text-on-surface'}`}
                  >
                    <t.icon className="h-4 w-4 text-on-surface-variant" /> {t.label}
                    {t.id === 'alerts' && alertCount > 0 && <span className="ml-auto rounded-full bg-amber-500 px-1.5 text-[10px] font-bold text-white">{alertCount}</span>}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* ── Modals ───────────────────────────────────────────────────────────── */}
      <NewTransferModal open={isTransferModalOpen} onClose={() => setIsTransferModalOpen(false)} />
      <NewAdjustmentModal open={isAdjustmentModalOpen} onClose={() => setIsAdjustmentModalOpen(false)} />

      {/* ── Tab Views ────────────────────────────────────────────────────────── */}
      {tab === 'requests' && <MaterialRequestsTab openNew={newRequest} />}
      {tab === 'inward' && <MaterialInwardTab openNew={newInward} />}
      {tab === 'tank-levels' && <TankLevelsTab />}
      {tab === 'tanks' && <TankRegisterTab />}
      {tab === 'suppliers' && <SuppliersTab />}
      {tab === 'stock' && <StockControlTab />}
      {tab === 'boms' && <PackingBomsTab />}
      {fullInventory && (
        <>
          {tab === 'warehouse' && <StockTab onOpenAdjustment={() => setIsAdjustmentModalOpen(true)} />}
          {tab === 'alerts' && <AlertsTab onOpenAdjustment={() => setIsAdjustmentModalOpen(true)} />}
          {tab === 'transfers' && <TransfersTab onOpenTransfer={() => setIsTransferModalOpen(true)} />}
          {tab === 'transit' && <TransitTab />}
          {tab === 'adjustments' && <AdjustmentsTab onOpenAdjustment={() => setIsAdjustmentModalOpen(true)} />}
          {tab === 'counts' && <CountsTab />}
          {tab === 'locations' && <LocationsTab />}
          {tab === 'bins' && <BinsTab />}
          {tab === 'reorder' && <ReorderTab />}
          {tab === 'aging' && <AgingTab />}
          {tab === 'trace' && <TraceabilityTab />}
          {tab === 'movements' && <MovementsTab />}
        </>
      )}
    </div>
  );
}

function StockTab({ onOpenAdjustment }: { onOpenAdjustment: () => void }) {
  const { data, isLoading } = useBalances();
  const [search, setSearch] = useState('');

  if (isLoading) return <Skeleton className="h-[300px]" />;
  const balances = data?.data || [];

  const filtered = balances.filter((b) => {
    if (!search.trim()) return true;
    const s = search.toLowerCase();
    const name = (b.productId?.name || '').toLowerCase();
    const sku = (b.productId?.sku || '').toLowerCase();
    const loc = (b.locationId?.name || '').toLowerCase();
    return name.includes(s) || sku.includes(s) || loc.includes(s);
  });

  return (
    <Card className="shadow-xs">
      <CardHeader
        title="Stock on Hand"
        action={
          <div className="flex items-center gap-3">
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search product name, SKU or location..."
              className="w-72 h-9 text-xs"
            />
            <Button size="sm" variant="outline" className="shrink-0 whitespace-nowrap" onClick={onOpenAdjustment}>
              <Plus className="h-3.5 w-3.5 mr-1" /> Quick Adjust
            </Button>
          </div>
        }
      />
      <CardBody className="p-0">
        <div className="overflow-auto max-h-[500px]">
          <table className="w-full min-w-[1400px] whitespace-nowrap text-sm text-left">
            <thead className="text-xs uppercase bg-muted/50 sticky top-0 border-b">
              <tr>
                <th className="px-4 py-3">Product Name</th>
                <th className="px-4 py-3">SKU</th>
                {/* What a picker actually needs to identify the drum in front
                    of them — the name alone does not distinguish a grade or a
                    pack size. */}
                <th className="px-4 py-3">Division</th>
                <th className="px-4 py-3">Group</th>
                <th className="px-4 py-3">Grade</th>
                <th className="px-4 py-3">Pack Size</th>
                <th className="px-4 py-3">Location</th>
                <th className="px-4 py-3">Stock Status</th>
                <th className="px-4 py-3 text-right">On Hand</th>
                <th className="px-4 py-3 text-right">Available</th>
                <th className="px-4 py-3 text-right">Avg Cost</th>
                <th className="px-4 py-3 text-right">Total Value</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={12} className="px-4 py-10 text-center text-muted-foreground">
                    No stock items match your search filter.
                  </td>
                </tr>
              )}
              {filtered.map((b) => {
                const onHand = b.quantityOnHand || 0;
                const avail = b.quantityAvailable || 0;
                const alertQty = b.productId?.alertQuantity || 5;
                const isOut = avail <= 0;
                const isLow = avail <= alertQty && !isOut;

                return (
                  <tr key={b._id} className="hover:bg-muted/20">
                    <td className="min-w-[240px] whitespace-normal px-4 py-3 font-medium text-foreground">
                      {b.productId?.name || 'Unnamed Item'}
                      {b.variantId?.name && (
                        <span className="ml-2 text-xs text-muted-foreground font-normal">
                          ({b.variantId.name})
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 font-mono text-xs text-muted-foreground">
                      {b.productId?.sku || '—'}
                    </td>
                    <td className="px-4 py-3 text-muted-foreground">{b.productId?.divisionId?.name || '—'}</td>
                    <td className="px-4 py-3 text-muted-foreground">{b.productId?.groupId?.name || '—'}</td>
                    {/* The MasterItem name where one is linked, else the older
                        flat column, so pre-taxonomy products still read. */}
                    <td className="px-4 py-3 text-muted-foreground">{b.productId?.gradeId?.name || b.productId?.grade || '—'}</td>
                    <td className="px-4 py-3 text-muted-foreground">{b.productId?.packSizeId?.name || b.productId?.packSize || '—'}</td>
                    <td className="px-4 py-3">
                      <span className="inline-flex items-center gap-1 text-xs bg-slate-100 dark:bg-slate-800 px-2 py-0.5 rounded font-medium">
                        <MapPin className="h-3 w-3 text-muted-foreground" />
                        {b.locationId?.name || 'Main Warehouse'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      {isOut ? (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-red-100 text-red-800 dark:bg-red-950 dark:text-red-300">
                          Out of Stock
                        </span>
                      ) : isLow ? (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300">
                          Low Stock
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300">
                          In Stock
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right font-medium">{onHand}</td>
                    <td className="px-4 py-3 text-right font-bold">{avail}</td>
                    <td className="px-4 py-3 text-right font-mono">
                      {formatINRPaise(b.averageCost || 0)}
                    </td>
                    <td className="px-4 py-3 text-right font-mono font-semibold text-emerald-700 dark:text-emerald-400">
                      ₹{(b.totalValue || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </CardBody>
    </Card>
  );
}

function AlertsTab({ onOpenAdjustment }: { onOpenAdjustment: () => void }) {
  const { data, isLoading } = useAlerts();
  const reorderMut = useCreateReorderPO();
  if (isLoading) return <Skeleton className="h-[300px]" />;
  const alerts = data?.alerts || [];

  const handleCreatePO = async (productId: string, productName: string) => {
    try {
      const result = await reorderMut.mutateAsync(productId);
      toast.success(`Draft PO created for ${productName} (qty: ${result.reorderQty}). Check Procurement.`);
    } catch (err: any) {
      toast.error(err?.response?.data?.error || 'Could not create PO');
    }
  };

  return (
    <Card className="shadow-xs">
      <CardHeader
        title={`Low Stock Alerts (${alerts.length})`}
        action={
          <Button size="sm" variant="outline" onClick={onOpenAdjustment}>
            <Plus className="h-3.5 w-3.5 mr-1" /> Adjust Stock
          </Button>
        }
      />
      <CardBody className="p-0">
        <div className="overflow-auto max-h-[500px]">
          <table className="w-full text-sm text-left">
            <thead className="text-xs uppercase bg-muted/50 sticky top-0 border-b">
              <tr>
                <th className="px-4 py-3">Product Name</th>
                <th className="px-4 py-3">SKU</th>
                <th className="px-4 py-3 text-right">Alert Threshold</th>
                <th className="px-4 py-3 text-right">Current Stock</th>
                <th className="px-4 py-3 text-right">Deficit</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {alerts.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-4 py-10 text-center text-emerald-600 font-semibold">
                    🎉 All inventory levels are healthy! No products are currently below reorder thresholds.
                  </td>
                </tr>
              )}
              {alerts.map((a) => (
                <tr key={a.productId} className="hover:bg-amber-50/40 dark:hover:bg-amber-950/20">
                  <td className="px-4 py-3 font-semibold text-foreground">{a.name}</td>
                  <td className="px-4 py-3 font-mono text-xs text-muted-foreground">{a.sku}</td>
                  <td className="px-4 py-3 text-right font-medium">{a.alertQuantity}</td>
                  <td className="px-4 py-3 text-right font-bold text-red-600">{a.currentStock}</td>
                  <td className="px-4 py-3 text-right font-extrabold text-red-700">−{a.deficit}</td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex justify-end gap-2">
                      <Button size="sm" variant="outline" onClick={() => handleCreatePO(a.productId, a.name)} disabled={reorderMut.isPending}>
                        <ShoppingCart className="h-3.5 w-3.5 mr-1" /> Create PO
                      </Button>
                      <Button size="sm" variant="outline" className="text-xs" onClick={onOpenAdjustment}>
                        Adjust Stock
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardBody>
    </Card>
  );
}

/* ── Locations master ────────────────────────────────────────────────────── */
function LocationsTab() {
  const { data: locations = [], isLoading } = useLocations();
  const createLocation = useCreateLocation();
  const user = useUser();
  const canManage = user?.role === 'superadmin' || user?.role === 'admin';
  const [open, setOpen] = useState(false);
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [facilityType, setFacilityType] = useState('warehouse');
  const [parentId, setParentId] = useState('');

  const submit = () => {
    if (!code.trim() || !name.trim()) { toast.error('Code and name are required.'); return; }
    createLocation.mutate(
      {
        code: code.trim(), name: name.trim(), city: city.trim(), state: state.trim(),
        facilityType, parentId: parentId || undefined,
      },
      {
        onSuccess: () => {
          toast.success('Location created.');
          setCode(''); setName(''); setCity(''); setState('');
          setFacilityType('warehouse'); setParentId(''); setOpen(false);
        },
        onError: (err) => toast.error(errorMessage(err, 'Could not create the location.')),
      },
    );
  };

  if (isLoading) return <Skeleton className="h-[300px]" />;

  return (
    <>
      <Modal
        open={open}
        onClose={() => setOpen(false)}
        title="New Location"
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={submit} disabled={createLocation.isPending}>
              {createLocation.isPending ? 'Saving…' : 'Create Location'}
            </Button>
          </div>
        }
      >
        <div className="flex flex-col gap-3">
          <div className="grid grid-cols-2 gap-3">
            <Input label="Code" name="loc-code" value={code} onChange={(e) => setCode(e.target.value)} placeholder="WH-01" />
            <Input label="Name" name="loc-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Main Warehouse" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Input label="City" name="loc-city" value={city} onChange={(e) => setCity(e.target.value)} />
            <Input label="State" name="loc-state" value={state} onChange={(e) => setState(e.target.value)} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Select
              label="Facility Type"
              name="loc-facility-type"
              value={facilityType}
              onChange={(e) => setFacilityType(e.target.value)}
              options={FACILITY_TYPE_OPTIONS}
            />
            <Select
              label="Parent Location"
              name="loc-parent"
              value={parentId}
              onChange={(e) => setParentId(e.target.value)}
              options={[
                { label: 'None (top level)', value: '' },
                ...locations.map(l => ({ label: `${l.name} (${l.code})`, value: l.id })),
              ]}
            />
          </div>
          <p className="text-body-sm text-on-surface-variant">
            Locations are the facilities that stock is held in and transferred between. Use a parent to nest a
            sub-warehouse inside a larger site; racks, shelves, bins and tanks are managed on the Bins &amp; Tanks tab.
          </p>
        </div>
      </Modal>

      <Card className="shadow-xs">
        <CardHeader title="Business Locations" action={canManage ? <Button size="sm" onClick={() => setOpen(true)}><Plus className="h-4 w-4 mr-1" />New Location</Button> : undefined} />
        <CardBody className="p-0">
          <div className="overflow-auto max-h-[500px]">
            <table className="w-full text-sm text-left">
              <thead className="text-xs uppercase bg-muted/50 sticky top-0 border-b">
                <tr><th className="px-4 py-3">Code</th><th className="px-4 py-3">Name</th><th className="px-4 py-3">Type</th><th className="px-4 py-3">Address</th><th className="px-4 py-3">Status</th></tr>
              </thead>
              <tbody className="divide-y">
                {locations.length === 0 && (
                  <tr><td colSpan={5} className="px-4 py-8 text-center text-muted-foreground">
                    No locations yet. Create one before recording stock transfers.
                  </td></tr>
                )}
                {locations.map(l => (
                  <tr key={l.id} className="hover:bg-muted/20">
                    <td className="px-4 py-3 font-mono text-xs">{l.code}</td>
                    <td className="px-4 py-3 font-medium">
                      {l.name}
                      {l.parentId && <span className="ml-1.5 text-xs text-muted-foreground">(sub-location)</span>}
                    </td>
                    <td className="px-4 py-3 text-muted-foreground capitalize">
                      {(l.facilityType || 'warehouse').replace(/_/g, ' ')}
                    </td>
                    <td className="px-4 py-3 text-muted-foreground">{l.addressText || '—'}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold ${l.isActive ? 'bg-emerald-100 text-emerald-800' : 'bg-gray-100 text-gray-600'}`}>
                        {l.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardBody>
      </Card>
    </>
  );
}

import { useProductSearch } from '@/features/pos/hooks/usePosApi';
import { Search, Trash2 } from 'lucide-react';

import { PageHeader } from '@/shared/components/ui/PageHeader';
import { formatINRPaise } from '@/shared/utils/currency';
import { StatCard, StatGrid } from '@/shared/components/ui/StatCard';
interface AdjustmentItem {
  productId: string;
  name: string;
  sku: string;
  type: 'increase' | 'decrease';
  quantity: number;
}

function SimpleAdjustmentProductPicker({
  items,
  onChange,
}: {
  items: AdjustmentItem[];
  onChange: (items: AdjustmentItem[]) => void;
}) {
  const [query, setQuery] = useState('');
  const { data: results = [] } = useProductSearch(query);

  const addProduct = (product: { _id: string; name: string; sku?: string }) => {
    const existingIndex = items.findIndex((i) => i.productId === product._id);
    if (existingIndex >= 0) {
      onChange(
        items.map((item, idx) =>
          idx === existingIndex ? { ...item, quantity: item.quantity + 1 } : item
        )
      );
    } else {
      onChange([
        ...items,
        {
          productId: product._id,
          name: product.name,
          sku: product.sku || '',
          type: 'increase',
          quantity: 1,
        },
      ]);
    }
    setQuery('');
  };

  const updateItem = (index: number, patch: Partial<AdjustmentItem>) => {
    onChange(items.map((item, idx) => (idx === index ? { ...item, ...patch } : item)));
  };

  const removeItem = (index: number) => {
    onChange(items.filter((_, idx) => idx !== index));
  };

  return (
    <div className="space-y-3">
      <div className="relative">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search product by name or SKU to add..."
          className="pl-9 text-sm"
        />
        {query && results.length > 0 && (
          <div className="absolute z-50 mt-1 max-h-56 w-full overflow-auto rounded-lg border bg-popover text-popover-foreground shadow-lg">
            {results.map((p) => (
              <button
                key={p._id}
                type="button"
                onClick={() => addProduct(p)}
                className="flex w-full items-center justify-between px-3.5 py-2.5 text-left text-sm hover:bg-accent hover:text-accent-foreground border-b last:border-0"
              >
                <div>
                  <span className="font-semibold">{p.name}</span>
                  {p.sku && <span className="ml-2 font-mono text-xs text-muted-foreground">({p.sku})</span>}
                </div>
                <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded font-semibold">+ Add</span>
              </button>
            ))}
          </div>
        )}
      </div>

      {items.length === 0 ? (
        <div className="p-6 text-center border border-dashed rounded-lg text-sm text-muted-foreground bg-muted/20">
          No products selected yet. Search above to add items to adjust.
        </div>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-border">
          <table className="w-full text-left text-sm">
            <thead className="bg-muted/50 text-xs font-semibold uppercase text-muted-foreground border-b">
              <tr>
                <th className="px-3.5 py-2.5">Product Name</th>
                <th className="w-40 px-3.5 py-2.5">Action Type</th>
                <th className="w-32 px-3.5 py-2.5 text-right">Adjustment Qty</th>
                <th className="w-12 px-3.5 py-2.5 text-center"></th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {items.map((item, index) => (
                <tr key={item.productId} className="hover:bg-muted/20">
                  <td className="px-3.5 py-2.5">
                    <div className="font-semibold text-foreground">{item.name}</div>
                    {item.sku && <div className="font-mono text-xs text-muted-foreground">{item.sku}</div>}
                  </td>
                  <td className="px-3.5 py-2.5">
                    <select
                      className={`flex h-8 w-full rounded-md border px-2 text-xs font-semibold shadow-sm ${
                        item.type === 'increase'
                          ? 'border-emerald-300 bg-emerald-50 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300'
                          : 'border-red-300 bg-red-50 text-red-800 dark:bg-red-950 dark:text-red-300'
                      }`}
                      value={item.type}
                      onChange={(e) => updateItem(index, { type: e.target.value as 'increase' | 'decrease' })}
                    >
                      <option value="increase">➕ Add Stock (+)</option>
                      <option value="decrease">➖ Deduct Stock (-)</option>
                    </select>
                  </td>
                  <td className="px-3.5 py-2.5">
                    <Input
                      type="number"
                      min="1"
                      className="text-right font-bold text-sm h-8"
                      value={item.quantity}
                      onChange={(e) => updateItem(index, { quantity: Math.max(1, Number(e.target.value) || 1) })}
                    />
                  </td>
                  <td className="px-3.5 py-2.5 text-center">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-8 w-8 p-0 text-destructive hover:bg-destructive/10"
                      onClick={() => removeItem(index)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

interface TransferItem {
  productId: string;
  name: string;
  sku: string;
  quantity: number;
}

function SimpleTransferProductPicker({
  items,
  onChange,
}: {
  items: TransferItem[];
  onChange: (items: TransferItem[]) => void;
}) {
  const [query, setQuery] = useState('');
  const { data: results = [] } = useProductSearch(query);

  const addProduct = (product: { _id: string; name: string; sku?: string }) => {
    const existingIndex = items.findIndex((i) => i.productId === product._id);
    if (existingIndex >= 0) {
      onChange(
        items.map((item, idx) =>
          idx === existingIndex ? { ...item, quantity: item.quantity + 1 } : item
        )
      );
    } else {
      onChange([
        ...items,
        {
          productId: product._id,
          name: product.name,
          sku: product.sku || '',
          quantity: 1,
        },
      ]);
    }
    setQuery('');
  };

  const updateItem = (index: number, patch: Partial<TransferItem>) => {
    onChange(items.map((item, idx) => (idx === index ? { ...item, ...patch } : item)));
  };

  const removeItem = (index: number) => {
    onChange(items.filter((_, idx) => idx !== index));
  };

  return (
    <div className="space-y-3">
      <div className="relative">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search product by name or SKU to transfer..."
          className="pl-9 text-sm"
        />
        {query && results.length > 0 && (
          <div className="absolute z-50 mt-1 max-h-56 w-full overflow-auto rounded-lg border bg-popover text-popover-foreground shadow-lg">
            {results.map((p) => (
              <button
                key={p._id}
                type="button"
                onClick={() => addProduct(p)}
                className="flex w-full items-center justify-between px-3.5 py-2.5 text-left text-sm hover:bg-accent hover:text-accent-foreground border-b last:border-0"
              >
                <div>
                  <span className="font-semibold">{p.name}</span>
                  {p.sku && <span className="ml-2 font-mono text-xs text-muted-foreground">({p.sku})</span>}
                </div>
                <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded font-semibold">+ Add</span>
              </button>
            ))}
          </div>
        )}
      </div>

      {items.length === 0 ? (
        <div className="p-6 text-center border border-dashed rounded-lg text-sm text-muted-foreground bg-muted/20">
          No products selected yet. Search above to add items to transfer.
        </div>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-border">
          <table className="w-full text-left text-sm">
            <thead className="bg-muted/50 text-xs font-semibold uppercase text-muted-foreground border-b">
              <tr>
                <th className="px-3.5 py-2.5">Product Name</th>
                <th className="w-36 px-3.5 py-2.5 text-right">Transfer Qty</th>
                <th className="w-12 px-3.5 py-2.5 text-center"></th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {items.map((item, index) => (
                <tr key={item.productId} className="hover:bg-muted/20">
                  <td className="px-3.5 py-2.5">
                    <div className="font-semibold text-foreground">{item.name}</div>
                    {item.sku && <div className="font-mono text-xs text-muted-foreground">{item.sku}</div>}
                  </td>
                  <td className="px-3.5 py-2.5">
                    <Input
                      type="number"
                      min="1"
                      className="text-right font-bold text-sm h-8"
                      value={item.quantity}
                      onChange={(e) => updateItem(index, { quantity: Math.max(1, Number(e.target.value) || 1) })}
                    />
                  </td>
                  <td className="px-3.5 py-2.5 text-center">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-8 w-8 p-0 text-destructive hover:bg-destructive/10"
                      onClick={() => removeItem(index)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* ── New stock transfer ──────────────────────────────────────────────────── */
function NewTransferModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { data: locations = [] } = useLocations(open);
  const createTransfer = useCreateTransfer();
  const [sourceLocationId, setSourceLocationId] = useState('');
  const [destinationLocationId, setDestinationLocationId] = useState('');
  const [notes, setNotes] = useState('');
  const [items, setItems] = useState<TransferItem[]>([]);

  const submit = () => {
    if (!sourceLocationId || !destinationLocationId) { toast.error('Select both source and destination locations.'); return; }
    if (sourceLocationId === destinationLocationId) { toast.error('Source and destination must be different.'); return; }
    if (items.length === 0) { toast.error('Add at least one product to transfer.'); return; }
    createTransfer.mutate(
      {
        sourceLocationId,
        destinationLocationId,
        notes,
        lines: items.map(i => ({ productId: i.productId, quantity: i.quantity })),
      },
      {
        onSuccess: () => {
          toast.success('Stock transfer created.');
          setSourceLocationId(''); setDestinationLocationId(''); setNotes(''); setItems([]);
          onClose();
        },
        onError: (err) => toast.error(errorMessage(err, 'Could not create the transfer.')),
      },
    );
  };

  const options = locations.map(l => ({ label: `${l.name} (${l.code})`, value: l.id }));

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Create Inter-Warehouse Stock Transfer"
      size="lg"
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={createTransfer.isPending} className="bg-primary hover:bg-primary-dim text-white font-semibold">
            {createTransfer.isPending ? 'Saving Transfer…' : 'Save Stock Transfer'}
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-4">
        {locations.length < 2 && (
          <div className="rounded-lg bg-amber-50 dark:bg-amber-950/40 p-3 text-xs text-amber-800 dark:text-amber-300 border border-amber-200">
            ⚠️ You need at least two active locations to transfer stock between warehouses.
          </div>
        )}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Select label="Source Warehouse (From)" name="tr-source" options={options} placeholder="Select source location" value={sourceLocationId} onChange={(e) => setSourceLocationId(e.target.value)} />
          <Select label="Destination Warehouse (To)" name="tr-dest" options={options} placeholder="Select destination location" value={destinationLocationId} onChange={(e) => setDestinationLocationId(e.target.value)} />
        </div>
        <div className="space-y-1">
          <label className="text-xs font-semibold uppercase text-muted-foreground">Select Products to Transfer</label>
          <SimpleTransferProductPicker items={items} onChange={setItems} />
        </div>
        <Input label="Transfer Notes / Reference" name="tr-notes" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="e.g. Dispatch via BlueDart express, Ref #TR-9901" />
      </div>
    </Modal>
  );
}

/* ── New stock adjustment ────────────────────────────────────────────────── */
function NewAdjustmentModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { data: locations = [] } = useLocations(open);
  const createAdjustment = useCreateAdjustment();
  const [locationId, setLocationId] = useState('');
  const [reasonCode, setReasonCode] = useState('correction');
  const [customReason, setCustomReason] = useState('');
  const [recoveredAmount, setRecoveredAmount] = useState('');
  const [items, setItems] = useState<AdjustmentItem[]>([]);

  const selectedReason = ADJUSTMENT_REASONS.find(r => r.value === reasonCode);
  // reasonCode drives GL account selection; the narrative is what a reviewer reads.
  const effectiveReason = customReason.trim() || selectedReason?.label || '';

  const submit = () => {
    if (items.length === 0) { toast.error('Add at least one product to adjust.'); return; }
    if (!reasonCode) { toast.error('Choose a reason for the adjustment.'); return; }

    const recovered = Number(recoveredAmount) || 0;
    if (recovered < 0) { toast.error('Recovered amount cannot be negative.'); return; }

    const payloadLines = items.map((item) => ({
      productId: item.productId,
      adjustedQuantity: item.type === 'increase' ? Math.abs(item.quantity) : -Math.abs(item.quantity),
    }));

    createAdjustment.mutate(
      {
        locationId: locationId || undefined,
        reasonCode,
        reason: effectiveReason,
        recoveredAmount: recovered,
        lines: payloadLines,
      },
      {
        onSuccess: () => {
          toast.success('Adjustment saved as draft. Submit and approve it to post.');
          setLocationId(''); setReasonCode('correction'); setCustomReason('');
          setRecoveredAmount(''); setItems([]);
          onClose();
        },
        onError: (err) => toast.error(errorMessage(err, 'Could not create the adjustment.')),
      },
    );
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Create Stock Adjustment"
      size="lg"
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={createAdjustment.isPending} className="bg-primary hover:bg-primary-dim text-white font-semibold">
            {createAdjustment.isPending ? 'Saving Adjustment…' : 'Save Stock Adjustment'}
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-4">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Select
            label="Target Location / Warehouse"
            name="adj-location"
            options={locations.map(l => ({ label: `${l.name} (${l.code})`, value: l.id }))}
            placeholder="Select location"
            value={locationId}
            onChange={(e) => setLocationId(e.target.value)}
          />
          <Select
            label="Adjustment Reason"
            name="adj-reason-code"
            value={reasonCode}
            onChange={(e) => setReasonCode(e.target.value)}
            options={ADJUSTMENT_REASONS.map(r => ({ label: r.label, value: r.value }))}
          />
        </div>

        {selectedReason && (
          <p className="rounded-md bg-muted/40 px-3 py-2 text-xs text-muted-foreground">
            Posts <span className="font-semibold">{selectedReason.posting}</span>
          </p>
        )}

        <Input
          label="Reason Details (optional)"
          name="adj-custom-reason"
          value={customReason}
          onChange={(e) => setCustomReason(e.target.value)}
          placeholder={`Defaults to "${selectedReason?.label ?? ''}" if left blank`}
        />

        <Input
          label="Recovered Amount (insurance / salvage)"
          name="adj-recovered"
          type="number"
          min="0"
          step="0.01"
          value={recoveredAmount}
          onChange={(e) => setRecoveredAmount(e.target.value)}
          placeholder="0.00"
        />
        <p className="text-xs text-muted-foreground -mt-2">
          Recovery proceeds post to recovery income on their own journal, so they stay visible
          separately from the inventory loss rather than netting against it.
        </p>

        <div className="space-y-2">
          <label className="text-xs font-semibold uppercase text-muted-foreground">Products & Quantities</label>
          <SimpleAdjustmentProductPicker items={items} onChange={setItems} />
        </div>
      </div>
    </Modal>
  );
}

function TransfersTab({ onOpenTransfer }: { onOpenTransfer: () => void }) {
  const { data, isLoading } = useTransfers();
  const actionMut = useTransferAction();
  if (isLoading) return <Skeleton className="h-[300px]" />;
  const transfers = data?.data || [];
  const statusBadge = (s: string) => {
    const c: Record<string, string> = {
      draft: 'bg-gray-100 text-gray-600', submitted: 'bg-blue-100 text-blue-800',
      approved: 'bg-indigo-100 text-indigo-800', picking: 'bg-sky-100 text-sky-800',
      packed: 'bg-cyan-100 text-cyan-800', in_transit: 'bg-yellow-100 text-yellow-800',
      shipped: 'bg-violet-100 text-violet-800', partially_received: 'bg-amber-100 text-amber-800',
      received: 'bg-green-100 text-green-800', closed: 'bg-slate-200 text-slate-700',
      cancelled: 'bg-red-100 text-red-600', rejected: 'bg-red-100 text-red-600',
    };
    return <span className={`px-2 py-0.5 rounded-full text-xs font-medium capitalize ${c[s] || 'bg-gray-100'}`}>{s.replace(/_/g, ' ')}</span>;
  };

  /** Lifecycle steps offered for a given transfer status, in display order. */
  const stepsFor = (status: string): { action: string; label: string; done: string }[] => {
    switch (status) {
      case 'draft': return [{ action: 'submit', label: 'Submit', done: 'Transfer submitted.' }];
      case 'submitted': return [
        { action: 'approve', label: 'Approve', done: 'Transfer approved.' },
        { action: 'reject', label: 'Reject', done: 'Transfer rejected.' },
      ];
      case 'approved': return [
        { action: 'pick', label: 'Pick', done: 'Marked as picking.' },
        { action: 'dispatch', label: 'Dispatch', done: 'Dispatched — stock is now in transit.' },
      ];
      case 'picking': return [
        { action: 'pack', label: 'Pack', done: 'Marked as packed.' },
        { action: 'dispatch', label: 'Dispatch', done: 'Dispatched — stock is now in transit.' },
      ];
      case 'packed': return [{ action: 'dispatch', label: 'Dispatch', done: 'Dispatched — stock is now in transit.' }];
      case 'in_transit': return [
        { action: 'receive', label: 'Receive', done: 'Transfer received.' },
        { action: 'cancel', label: 'Cancel', done: 'Cancelled — stock returned to source.' },
      ];
      case 'partially_received': return [
        { action: 'receive', label: 'Receive Rest', done: 'Remaining stock received.' },
        { action: 'close', label: 'Close', done: 'Transfer closed.' },
      ];
      case 'received': return [{ action: 'close', label: 'Close', done: 'Transfer closed.' }];
      default: return [];
    }
  };

  const runStep = (id: string, step: { action: string; label: string; done: string }) => {
    actionMut.mutate({ id, action: step.action }, {
      onSuccess: () => toast.success(step.done),
      onError: (err) => toast.error(errorMessage(err, `Could not ${step.label.toLowerCase()} the transfer.`)),
    });
  };
  return (
    <Card className="shadow-xs"><CardHeader title="Stock Transfers" action={<Button size="sm" onClick={onOpenTransfer}><Plus className="h-4 w-4 mr-1" />New Transfer</Button>} /><CardBody className="p-0"><div className="overflow-auto max-h-[500px]"><table className="w-full text-sm text-left"><thead className="text-xs uppercase bg-muted/50 sticky top-0 border-b"><tr><th className="px-4 py-3">Date</th><th className="px-4 py-3">From → To</th><th className="px-4 py-3 text-right">Qty</th><th className="px-4 py-3">Status</th><th className="px-4 py-3">Actions</th></tr></thead>
    <tbody className="divide-y">{transfers.length === 0 && <tr><td colSpan={5} className="px-4 py-8 text-center text-muted-foreground">No transfers recorded.</td></tr>}{transfers.map(t => (<tr key={t._id} className="hover:bg-muted/20"><td className="px-4 py-3">{new Date(t.date).toLocaleDateString('en-IN')}</td><td className="px-4 py-3">{t.sourceLocationId?.name || '?'} → {t.destinationLocationId?.name || '?'}</td><td className="px-4 py-3 text-right font-medium">{t.totalQuantity}</td><td className="px-4 py-3">{statusBadge(t.status)}</td><td className="px-4 py-3"><div className="flex flex-wrap gap-1">{stepsFor(t.status).map(step => (<Button key={step.action} size="sm" variant="outline" disabled={actionMut.isPending} onClick={() => runStep(t._id, step)}>{step.label}</Button>))}{stepsFor(t.status).length === 0 && <span className="text-xs text-muted-foreground">—</span>}</div></td></tr>))}</tbody></table></div></CardBody></Card>
  );
}

function AdjustmentsTab({ onOpenAdjustment }: { onOpenAdjustment: () => void }) {
  const { data, isLoading } = useAdjustments();
  const actionMut = useAdjustmentAction();
  if (isLoading) return <Skeleton className="h-[300px]" />;
  const adjustments = data?.data || [];
  const statusBadge = (s: string) => { const c: Record<string,string> = { draft: 'bg-gray-100 text-gray-600', submitted: 'bg-blue-100 text-blue-800', approved: 'bg-indigo-100 text-indigo-800', posted: 'bg-green-100 text-green-800', rejected: 'bg-red-100 text-red-600' }; return <span className={`px-2 py-0.5 rounded-full text-xs font-medium capitalize ${c[s] || 'bg-gray-100'}`}>{s}</span>; };
  return (
    <Card className="shadow-xs"><CardHeader title="Stock Adjustments" action={<Button size="sm" onClick={onOpenAdjustment}><Plus className="h-4 w-4 mr-1" />New Adjustment</Button>} /><CardBody className="p-0"><div className="overflow-auto max-h-[500px]"><table className="w-full text-sm text-left"><thead className="text-xs uppercase bg-muted/50 sticky top-0 border-b"><tr><th className="px-4 py-3">Date</th><th className="px-4 py-3">Type</th><th className="px-4 py-3">Reason</th><th className="px-4 py-3 text-right">+Qty</th><th className="px-4 py-3 text-right">-Qty</th><th className="px-4 py-3">Status</th><th className="px-4 py-3">Actions</th></tr></thead>
    <tbody className="divide-y">{adjustments.length === 0 && <tr><td colSpan={7} className="px-4 py-8 text-center text-muted-foreground">No adjustments recorded.</td></tr>}{adjustments.map(a => (<tr key={a._id} className="hover:bg-muted/20"><td className="px-4 py-3">{new Date(a.date).toLocaleDateString('en-IN')}</td><td className="px-4 py-3 capitalize">{a.reasonCode ? a.reasonCode.replace(/_/g, ' ') : a.adjustmentType}</td><td className="px-4 py-3 truncate max-w-[150px]">{a.reason || '-'}{(a.recoveredAmount ?? 0) > 0 && <span className="ml-1 text-xs text-emerald-600">+recovery</span>}</td><td className="px-4 py-3 text-right text-green-600">{a.totalIncreaseQty || '-'}</td><td className="px-4 py-3 text-right text-red-600">{a.totalDecreaseQty || '-'}</td><td className="px-4 py-3">{statusBadge(a.status)}</td><td className="px-4 py-3"><div className="flex gap-1">{a.status === 'draft' && <Button size="sm" variant="outline" onClick={() => { actionMut.mutateAsync({ id: a._id, action: 'submit' }); toast.success('Submitted'); }}>Submit</Button>}{a.status === 'submitted' && <Button size="sm" variant="outline" onClick={() => { actionMut.mutateAsync({ id: a._id, action: 'approve' }); toast.success('Approved'); }}>Approve</Button>}{a.status === 'approved' && <Button size="sm" variant="outline" onClick={() => { actionMut.mutateAsync({ id: a._id, action: 'post' }); toast.success('Posted'); }}>Post</Button>}</div></td></tr>))}</tbody></table></div></CardBody></Card>
  );
}

function MovementsTab() {
  const { data, isLoading } = useMovements();
  if (isLoading) return <Skeleton className="h-[300px]" />;
  const movements = data?.data || [];
  return (
    <Card className="shadow-xs"><CardHeader title="Movement History" /><CardBody className="p-0"><div className="overflow-auto max-h-[500px]"><table className="w-full text-sm text-left"><thead className="text-xs uppercase bg-muted/50 sticky top-0 border-b"><tr><th className="px-4 py-3">Date</th><th className="px-4 py-3">Product</th><th className="px-4 py-3">Type</th><th className="px-4 py-3 text-right">In</th><th className="px-4 py-3 text-right">Out</th><th className="px-4 py-3">Reference</th><th className="px-4 py-3">By</th></tr></thead>
    <tbody className="divide-y">{movements.length === 0 && <tr><td colSpan={7} className="px-4 py-8 text-center text-muted-foreground">No movements recorded.</td></tr>}{movements.map(m => (<tr key={m._id} className="hover:bg-muted/20"><td className="px-4 py-3">{new Date(m.createdAt).toLocaleDateString('en-IN')}</td><td className="px-4 py-3 font-medium">{m.productId?.name || '-'}</td><td className="px-4 py-3 capitalize text-xs">{m.movementType?.replace(/_/g, ' ')}</td><td className="px-4 py-3 text-right text-green-600 font-semibold">{m.quantityIn > 0 ? `+${m.quantityIn}` : ''}</td><td className="px-4 py-3 text-right text-red-600 font-semibold">{m.quantityOut > 0 ? `-${m.quantityOut}` : ''}</td><td className="px-4 py-3 text-xs">{m.referenceNumber || m.narration?.slice(0, 30) || '-'}</td><td className="px-4 py-3 text-xs">{m.createdBy?.display_name || '-'}</td></tr>))}</tbody></table></div></CardBody></Card>
  );
}
